import React, { useState } from 'react';
import { Mail, TreePine } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useLanguage } from '../context/LanguageContext';
import { requestPasswordReset } from '../services/api';

const ForgotPassword = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error(t('portalUi.login.emailRequired', 'Norādiet e-pastu'));
      return;
    }

    try {
      setIsSubmitting(true);
      await requestPasswordReset(email);
      setIsSent(true);
    } catch (error) {
      toast.error(t('portalUi.login.resetError', 'Neizdevās nosūtīt atjaunošanas saiti'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f2eb] via-[#f0f5ef] to-[#e8f0ea] flex items-center justify-center p-6" data-testid="portal-forgot-page">
      <Card className="w-full max-w-lg shadow-xl border-0 bg-white/90 backdrop-blur">
        <CardHeader className="text-center space-y-2">
          <div className="w-14 h-14 rounded-2xl bg-[#1F3D2B] text-white flex items-center justify-center mx-auto">
            <TreePine className="w-7 h-7" />
          </div>
          <CardTitle className="text-2xl font-semibold text-[#1F3D2B]" data-testid="portal-forgot-title">
            {t('portalUi.login.forgotTitle', 'Atjaunot paroli')}
          </CardTitle>
          <p className="text-sm text-[#2E2E2E]/60" data-testid="portal-forgot-subtitle">
            {t('portalUi.login.forgotSubtitle', 'Ievadiet savu e-pastu, lai saņemtu atjaunošanas saiti')}
          </p>
        </CardHeader>
        <CardContent>
          {isSent ? (
            <div className="space-y-4" data-testid="portal-forgot-success">
              <p className="text-sm text-[#2E2E2E]/70">
                {t('portalUi.login.forgotSuccess', 'Ja konts eksistē, saite ir nosūtīta uz norādīto e-pastu.')}
              </p>
              <Button className="w-full" onClick={() => navigate('/portal/login')} data-testid="portal-forgot-back-login">
                {t('portalUi.login.backToLogin', 'Atgriezties uz login')}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="portal-forgot-form">
              <div className="space-y-2">
                <Label htmlFor="forgot-email">{t('portalUi.login.emailLabel', 'E-pasts')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#2E2E2E]/40" />
                  <Input
                    id="forgot-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('portalUi.login.emailPlaceholder', 'Ievadiet e-pastu')}
                    className="pl-10 h-12"
                    required
                    data-testid="portal-forgot-email"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full h-12" disabled={isSubmitting} data-testid="portal-forgot-submit">
                {isSubmitting ? t('portalUi.common.loading', 'Ielādē...') : t('portalUi.login.sendReset', 'Nosūtīt saiti')}
              </Button>
              <Button type="button" variant="ghost" className="w-full" onClick={() => navigate('/portal/login')} data-testid="portal-forgot-cancel">
                {t('portalUi.login.backToLogin', 'Atgriezties uz login')}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
